/**
 * useFeatureFlag Hook
 *
 * Central hook for feature flag management using FeatureFlip SDK.
 * Returns empty treatments when FeatureFlip context is not available.
 */

import { FeatureFlipContext } from '@ninjacart/feature-flip-sdk-react';
import { IDENTITY_TYPES, Identity } from '@ninjacart/feature-flip-sdk-core';
import { useEffect, useContext, useState } from 'react';

// Re-export IDENTITY_TYPES and Identity for convenience
export { IDENTITY_TYPES };
export type { Identity };

export type IdentityType = (typeof IDENTITY_TYPES)[keyof typeof IDENTITY_TYPES];

interface TreatmentResult {
  treatment: string;
  config: string | null;
}

interface TreatmentsWithConfig {
  [key: string]: TreatmentResult;
}

const EMPTY_TREATMENTS: TreatmentsWithConfig = {};

const DEFAULT_TREATMENT: TreatmentResult = {
  treatment: 'control',
  config: null,
};

/**
 * Creates a proxy object that returns default treatment for missing keys
 * This prevents "Cannot read properties of undefined" errors
 */
const createSafeTreatments = (
  treatments: TreatmentsWithConfig
): TreatmentsWithConfig => {
  return new Proxy(treatments, {
    get(target, prop: string | symbol) {
      if (typeof prop === 'symbol') {
        return Reflect.get(target, prop);
      }
      if (prop in target) {
        return target[prop];
      }
      return DEFAULT_TREATMENT;
    },
    ownKeys(target) {
      return Reflect.ownKeys(target);
    },
    getOwnPropertyDescriptor(target, prop) {
      return (
        Reflect.getOwnPropertyDescriptor(target, prop) || {
          configurable: true,
          enumerable: true,
          value: DEFAULT_TREATMENT,
        }
      );
    },
  });
};

/**
 * Utility function to merge multiple treatment objects safely
 * Use this when combining treatments from multiple useFeatureFlags calls
 *
 * @param treatmentObjects - Treatment objects to merge
 * @returns Merged treatments wrapped in safe proxy
 *
 * @example
 * const merged = mergeTreatments(treatments, cityCategoryTreatments);
 */
export const mergeTreatments = (
  ...treatmentObjects: TreatmentsWithConfig[]
): TreatmentsWithConfig => {
  const merged: TreatmentsWithConfig = {};
  for (const obj of treatmentObjects) {
    const keys = Object.keys(obj);
    for (const key of keys) {
      merged[key] = obj[key];
    }
  }
  return createSafeTreatments(merged);
};

/**
 * Hook to check if FeatureFlip context is available
 */
export const useFeatureFlipContextAvailable = (): boolean => {
  const context = useContext(FeatureFlipContext);
  return context !== null && context !== undefined;
};

/**
 * Custom hook that uses FeatureFlip client directly from context
 * This avoids calling useFeatureFlipTreatments which throws when context is missing
 */
const useFeatureFlipTreatmentsDirect = (
  names: string[],
  identity?: Identity
): { treatments: TreatmentsWithConfig; isReady: boolean } => {
  const context = useContext(FeatureFlipContext);
  const [treatments, setTreatments] =
    useState<TreatmentsWithConfig>(EMPTY_TREATMENTS);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!context || !context.client || names.length === 0) {
      setTreatments(EMPTY_TREATMENTS);
      setIsReady(false);
      return;
    }

    try {
      const client = context.client;
      const result: TreatmentsWithConfig = {};

      for (const name of names) {
        const isOn = client.isEnabled(name);
        result[name] = {
          treatment: isOn ? 'on' : 'off',
          config: null,
        };
      }

      setTreatments(result);
      setIsReady(context.isReady);
    } catch (error) {
      console.warn('Error fetching FeatureFlip treatments:', error);
      setTreatments(EMPTY_TREATMENTS);
      setIsReady(false);
    }
  }, [
    context,
    context?.client,
    context?.isReady,
    names.join(','),
    identity?.value,
  ]);

  return { treatments, isReady };
};

/**
 * Custom hook that provides feature flag treatments using FeatureFlip SDK.
 * Returns empty treatments when FeatureFlip context is not available.
 *
 * @param flagNames - Array of flag names to fetch
 * @param identity - Identity object for FeatureFlip SDK (optional)
 * @returns Treatments object with flag values
 */
export const useFeatureFlags = (
  flagNames: string[],
  identity?: Identity
): TreatmentsWithConfig => {
  const featureFlipContext = useContext(FeatureFlipContext);
  const isFeatureFlipAvailable =
    featureFlipContext !== null && featureFlipContext !== undefined;

  const { treatments: featureFlipTreatments } = useFeatureFlipTreatmentsDirect(
    flagNames,
    identity
  );

  if (isFeatureFlipAvailable) {
    return createSafeTreatments(featureFlipTreatments);
  }

  return createSafeTreatments(EMPTY_TREATMENTS);
};

/**
 * Hook to get a single feature flag value
 *
 * @param flagName - The name of the flag to check
 * @param identity - Identity object for FeatureFlip SDK (optional)
 * @returns boolean - true if flag is 'on', false otherwise
 */
export const useFeatureFlagEnabled = (
  flagName: string,
  identity?: Identity
): boolean => {
  const treatments = useFeatureFlags([flagName], identity);
  return treatments[flagName]?.treatment === 'on';
};

/**
 * Hook to get treatment value for a single flag
 *
 * @param flagName - The name of the flag
 * @param identity - Identity object for FeatureFlip SDK (optional)
 * @returns The treatment value string
 */
export const useFeatureFlagTreatment = (
  flagName: string,
  identity?: Identity
): string => {
  const treatments = useFeatureFlags([flagName], identity);
  return treatments[flagName]?.treatment || 'control';
};

/**
 * Hook to check if FeatureFlip context is available and ready
 * @returns boolean indicating if FeatureFlip is available
 */
export const useIsFeatureFlagReady = (): boolean => {
  const context = useContext(FeatureFlipContext);
  return context !== null && context !== undefined;
};

export default useFeatureFlags;
